import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Newspaper, Users, IdCard, FolderTree, LogOut, ExternalLink, Menu, X, Mail, Megaphone, Send, Settings, Sprout, Calendar, MessageSquare, Radio, Crown, IndianRupee, BarChart3, PieChart, Sparkles, FileText, MapPin, Trophy, Heart, Video, Film, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import BrandLogo from "@/components/BrandLogo";

const NAV = [
  { to: "/admin", label: "डैशबोर्ड", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/admin/analytics", label: "विश्लेषिकी", icon: BarChart3, id: "analytics" },
  { to: "/admin/news", label: "समाचार", icon: Newspaper, id: "news" },
  { to: "/admin/categories", label: "श्रेणियाँ", icon: FolderTree, id: "categories" },
  { to: "/admin/mandi", label: "मंडी भाव", icon: Sprout, id: "mandi" },
  { to: "/admin/events", label: "आयोजन", icon: Calendar, id: "events" },
  { to: "/admin/retailers", label: "रिटेलर", icon: Users, id: "retailers" },
  { to: "/admin/staff", label: "स्टाफ / ID कार्ड", icon: IdCard, id: "staff" },
  { to: "/admin/reporter-approvals", label: "रिपोर्टर सबमिशन", icon: Send, id: "reporter-approvals" },
  { to: "/admin/reporter-map", label: "कवरेज मानचित्र", icon: MapPin, id: "reporter-map" },
  { to: "/admin/reporter-ratings", label: "रिपोर्टर रैंकिंग", icon: Trophy, id: "reporter-ratings" },
  { to: "/admin/reporter-chat", label: "रिपोर्टर चैट", icon: MessageSquare, id: "reporter-chat" },
  { to: "/admin/contributor", label: "पाठक योगदान", icon: Heart, id: "contributor" },
  { to: "/admin/editor-bytes", label: "Editor वीडियो बाइट्स", icon: Video, id: "editor-bytes" },
  { to: "/admin/video-editor", label: "Video Editor", icon: Film, id: "video-editor" },
  { to: "/admin/promo-video", label: "Grand Opening Promo 🎬", icon: Sparkles, id: "promo-video" },
  { to: "/admin/auto-news", label: "AI Auto-News 🤖", icon: Bot, id: "auto-news" },
  { to: "/admin/owner-card", label: "प्रधान संपादक ID", icon: Crown, id: "owner-card" },
  { to: "/admin/ads", label: "विज्ञापन", icon: Megaphone, id: "ads" },
  { to: "/admin/pdf-ads", label: "e-Paper प्रचार", icon: FileText, id: "pdf-ads" },
  { to: "/admin/directory-revenue", label: "व्यापार Revenue 💰", icon: IndianRupee, id: "directory-revenue" },
  { to: "/admin/whatsapp-broadcast", label: "WhatsApp Broadcast", icon: Send, id: "whatsapp-broadcast" },
  { to: "/admin/ai-image", label: "AI Photo Studio ✨", icon: Sparkles, id: "ai-image" },
  { to: "/admin/wishes", label: "शुभकामनाएँ", icon: Sparkles, id: "wishes" },
  { to: "/admin/classifieds", label: "पेड विज्ञापन (₹)", icon: IndianRupee, id: "classifieds" },
  { to: "/admin/newsletter", label: "न्यूज़लेटर", icon: Send, id: "newsletter" },
  { to: "/admin/push", label: "पुश सूचना", icon: Radio, id: "push" },
  { to: "/admin/live-blog", label: "लाइव ब्लॉग", icon: Radio, id: "live-blog" },
  { to: "/admin/polls", label: "पोल्स", icon: PieChart, id: "polls" },
  { to: "/admin/comments", label: "टिप्पणियाँ", icon: MessageSquare, id: "comments" },
  { to: "/admin/messages", label: "संदेश", icon: Mail, id: "messages" },
  { to: "/admin/settings", label: "सेटिंग्स", icon: Settings, id: "settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => { await logout(); nav("/admin/login"); };

  const Sidebar = ({ mobile }) => (
    <aside className={`${mobile ? "fixed inset-y-0 left-0 z-50 w-64" : "hidden lg:flex w-64 shrink-0"} bg-white border-r border-slate-200 flex-col flex`}>
      <div className="p-5 border-b border-slate-200 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <BrandLogo size={40}/>
          <div>
            <div className="font-bold text-[#0F172A] leading-none text-sm tracking-tight">INDIAN-NEWS-21</div>
            <div className="text-[10px] uppercase tracking-wider text-[#EA580C] font-bold mt-0.5">Admin</div>
          </div>
        </Link>
        {mobile && <button onClick={() => setOpen(false)} data-testid="sb-close"><X className="w-5 h-5 text-slate-500"/></button>}
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end}
            onClick={() => setOpen(false)}
            className={({isActive}) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm ${isActive ? "bg-[#0F172A] text-white" : "text-slate-700 hover:bg-slate-100"}`}
            data-testid={`nav-${n.id}`}>
            <n.icon className="w-4 h-4"/> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-200">
        <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-slate-500 hover:text-[#0F172A] px-3 py-2" data-testid="view-site">
          <ExternalLink className="w-3.5 h-3.5"/> साइट देखें
        </a>
        <div className="px-3 py-2 text-xs text-slate-500">
          <div className="text-slate-700 font-medium">{user?.name}</div>
          <div>{user?.email}</div>
        </div>
        <Button variant="outline" className="w-full mt-2" onClick={doLogout} data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-2"/> लॉगआउट
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen font-admin bg-[#F1F5F9] flex">
      <Sidebar mobile={false}/>
      {open && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setOpen(false)}></div>
          <Sidebar mobile={true}/>
        </>
      )}
      <main className="flex-1 overflow-auto">
        <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-30">
          <button onClick={() => setOpen(true)} data-testid="sb-open"><Menu className="w-5 h-5 text-slate-700"/></button>
          <div className="font-bold text-slate-800">Admin Panel</div>
        </div>
        <div className="max-w-7xl mx-auto p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
